import * as React from "react";
import { Scale } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api-client";
import { cn } from "@/lib/utils";
import type { Measure, MeasuresListResponse } from "@/features/rooms/types";
import type { ConfirmedLevel } from "@/lib/stufen-labels";

/**
 * MeasuresMultiselect — filtert Katalog-Maßnahmen nach Hitzestufe und
 * erlaubt Mehrfach-Auswahl. Roh-Codes werden nie angetippt — der User sieht
 * immer den Titel aus dem Katalog.
 *
 * - Pflicht-Maßnahmen (priority="pflicht") werden vorausgewählt + als
 *   "erforderlich" markiert (Scale-Icon, gelb/orange).
 * - Touch-Target ≥ 56px (minHeight) pro Item (AAA-konform für mobile).
 * - Lazy-load: Query läuft nur wenn `level` definiert ist.
 * - Bei Lade-Fehler: dezente Fehlermeldung, kein Formular-Block.
 *
 * Props:
 *   level           — aktuelle Vorschau-Stufe (0..3). undefined = kein Fetch.
 *   selectedCodes   — kontrolliertes Array der ausgewählten Codes.
 *   onChange        — wird mit dem neuen Array aufgerufen.
 *   className       — optionaler Wrapper-Klassen-Extend.
 */

const LEVEL_SLUG: Record<ConfirmedLevel, string> = {
  0: "normal",
  1: "soll",
  2: "muss",
  3: "kritisch",
};

export interface MeasuresMultiselectProps {
  level: ConfirmedLevel | undefined;
  selectedCodes: string[];
  onChange: (codes: string[]) => void;
  className?: string;
}

export function MeasuresMultiselect({
  level,
  selectedCodes,
  onChange,
  className,
}: MeasuresMultiselectProps): React.ReactElement {
  const slug = level !== undefined ? LEVEL_SLUG[level] : null;

  const catalog = useQuery({
    queryKey: ["measures", slug],
    queryFn: () => apiFetch<MeasuresListResponse>(`/v1/measures/${slug}`),
    enabled: slug !== null,
    staleTime: 60 * 60 * 1000, // Katalog ist quasi statisch
    retry: 1,
  });

  // Pflicht-Maßnahmen beim ersten Laden vorauswählen — sofern noch kein
  // explizites selectedCodes-Array kommt (leer = noch nicht berührt).
  const preselectedRef = React.useRef(false);
  React.useEffect(() => {
    if (preselectedRef.current) return;
    if (!catalog.data?.measures) return;
    if (selectedCodes.length > 0) {
      preselectedRef.current = true;
      return;
    }
    const pflichtCodes = catalog.data.measures
      .filter((m) => m.priority === "pflicht")
      .map((m) => m.code);
    if (pflichtCodes.length > 0) {
      onChange(pflichtCodes);
    }
    preselectedRef.current = true;
  }, [catalog.data, selectedCodes, onChange]);

  // Reset preselect-flag wenn sich die Stufe ändert, damit neue Pflicht-
  // Maßnahmen der neuen Stufe vorausgewählt werden.
  const prevLevel = React.useRef(level);
  if (prevLevel.current !== level) {
    prevLevel.current = level;
    preselectedRef.current = false;
  }

  function toggle(code: string) {
    if (selectedCodes.includes(code)) {
      onChange(selectedCodes.filter((c) => c !== code));
    } else {
      onChange([...selectedCodes, code]);
    }
  }

  if (level === undefined || slug === null) {
    return (
      <div className={cn("rounded-md bg-[color:var(--color-surface-elevated)] p-3 text-xs text-[color:var(--color-text-muted)]", className)}>
        Temperatur eingeben, um passende Maßnahmen anzuzeigen.
      </div>
    );
  }

  if (catalog.isPending) {
    return (
      <div className={cn("space-y-2", className)} aria-busy="true">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="h-14 animate-pulse rounded-md bg-[color:var(--color-surface-sunken)]"
          />
        ))}
      </div>
    );
  }

  if (catalog.isError || !catalog.data?.measures.length) {
    return (
      <div className={cn("rounded-md bg-[color:var(--color-surface-elevated)] p-3 text-xs text-[color:var(--color-text-muted)]", className)}>
        {catalog.isError
          ? "Maßnahmen-Katalog konnte nicht geladen werden."
          : "Für diese Stufe sind keine Maßnahmen im Katalog."}
      </div>
    );
  }

  const measures: Measure[] = catalog.data.measures;

  return (
    <ul
      role="group"
      aria-label="Dokumentierte Maßnahmen"
      className={cn("space-y-2", className)}
    >
      {measures.map((m) => {
        const checked = selectedCodes.includes(m.code);
        const isPflicht = m.priority === "pflicht";
        return (
          <li key={m.code}>
            <label
              className={cn(
                // Touch-target ≥ 56px (min-h-14)
                "flex min-h-14 cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors duration-150",
                checked
                  ? "border-[color:var(--color-brand-primary)] bg-[color:var(--color-brand-primary-soft)]"
                  : "border-[color:var(--color-border)] bg-white hover:bg-[color:var(--color-surface-elevated)]",
              )}
            >
              <input
                type="checkbox"
                className="mt-0.5 size-4 shrink-0 accent-[color:var(--color-brand-primary)]"
                checked={checked}
                onChange={() => toggle(m.code)}
              />
              <div className="flex-1 text-sm leading-snug">
                <span className="inline-flex items-center gap-1.5 font-medium text-[color:var(--color-text-primary)]">
                  {isPflicht && (
                    <Scale
                      aria-label="Pflicht-Maßnahme"
                      strokeWidth={2}
                      className="size-3.5 shrink-0 text-[color:var(--color-heat-3)]"
                    />
                  )}
                  {m.title}
                  {isPflicht && (
                    <span className="rounded bg-[color:var(--color-heat-3-soft)] px-1.5 py-0.5 text-[0.65rem] font-semibold uppercase tracking-wide text-[color:var(--color-text-primary)]">
                      erforderlich
                    </span>
                  )}
                </span>
                {m.description && (
                  <p className="mt-0.5 text-xs text-[color:var(--color-text-secondary)]">
                    {m.description}
                  </p>
                )}
                {m.legal_reference && (
                  <p className="mt-0.5 text-[0.65rem] text-[color:var(--color-text-muted)]">
                    {m.legal_reference}
                  </p>
                )}
              </div>
            </label>
          </li>
        );
      })}
    </ul>
  );
}

MeasuresMultiselect.displayName = "MeasuresMultiselect";
